import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { Select as UiSelect, type SelectOption } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { TagSelector } from './TagSelector';

/** Alinhado ao default da API `obras/buscar` (`maxCapitulos` omitido = 1000). */
export const CAPITULOS_MAX_PADRAO = 1000;

interface FilterOption {
    id: number;
    nome: string;
}

interface SearchAdvancedFiltersProps {
    statuses: FilterOption[];
    selectedStatuses: number[];
    onStatusChange: (statusIds: number[]) => void;
    tags: FilterOption[];
    selectedTags: number[];
    onTagsChange: (tagIds: number[]) => void;
    loadingTags: boolean;
    ordenarPor: string;
    ordenarOptions: SelectOption[];
    onOrdenarChange: (value: string) => void;
    minCapitulos: number;
    maxCapitulos: number;
    onCapitulosChange: (min: number, max: number) => void;
}

const customSelectStyles = {
    control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
        ...base,
        background: 'rgba(255, 255, 255, 0.03)',
        borderColor: state.isFocused ? 'rgba(249, 115, 22, 0.45)' : 'rgba(255, 255, 255, 0.1)',
        borderRadius: '0.75rem',
        padding: '0.15rem',
        boxShadow: 'none',
        minHeight: '42px',
    }),
    menu: (base: Record<string, unknown>) => ({
        ...base,
        background: '#0a0a0a',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '0.75rem',
        zIndex: 50,
    }),
    option: (base: Record<string, unknown>, state: { isSelected?: boolean; isFocused?: boolean }) => ({
        ...base,
        background: state.isSelected ? 'rgba(249, 115, 22, 0.15)' : state.isFocused ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
        color: state.isSelected ? '#fb923c' : 'rgba(255, 255, 255, 0.85)',
        cursor: 'pointer',
    }),
    multiValue: (base: Record<string, unknown>) => ({
        ...base,
        background: 'rgba(249, 115, 22, 0.1)',
        borderRadius: '0.5rem',
        border: '1px solid rgba(249, 115, 22, 0.2)',
    }),
    multiValueLabel: (base: Record<string, unknown>) => ({
        ...base,
        color: '#fdba74',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    multiValueRemove: (base: Record<string, unknown>) => ({
        ...base,
        color: '#fb923c',
        ':hover': {
            background: 'rgba(249, 115, 22, 0.2)',
            color: '#fff',
        },
    }),
    input: (base: Record<string, unknown>) => ({ ...base, color: 'white' }),
    placeholder: (base: Record<string, unknown>) => ({ ...base, color: 'rgba(255, 255, 255, 0.35)', fontSize: '0.8125rem' }),
};

export const SearchAdvancedFilters: React.FC<SearchAdvancedFiltersProps> = ({
    statuses,
    selectedStatuses,
    onStatusChange,
    tags,
    selectedTags,
    onTagsChange,
    loadingTags,
    ordenarPor,
    ordenarOptions,
    onOrdenarChange,
    minCapitulos,
    maxCapitulos,
    onCapitulosChange,
}) => {
    const [draftMin, setDraftMin] = useState(String(minCapitulos));
    const [draftMax, setDraftMax] = useState(String(maxCapitulos));

    useEffect(() => {
        setDraftMin(String(minCapitulos));
        setDraftMax(String(maxCapitulos));
    }, [minCapitulos, maxCapitulos]);

    const commitCapitulos = useCallback(() => {
        const min = Math.max(0, Math.floor(Number(draftMin)) || 0);
        let max = Math.floor(Number(draftMax));
        if (draftMax.trim() === '' || Number.isNaN(max)) max = CAPITULOS_MAX_PADRAO;
        max = Math.max(0, max);
        if (max < min) max = min;
        setDraftMin(String(min));
        setDraftMax(String(max));
        onCapitulosChange(min, max);
    }, [draftMin, draftMax, onCapitulosChange]);

    const capitulosFiltroAtivo = minCapitulos > 0 || maxCapitulos !== CAPITULOS_MAX_PADRAO;

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
                <div className="min-w-0">
                    <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wide text-white/40">Ordenar</label>
                    <UiSelect
                        value={ordenarPor}
                        options={ordenarOptions}
                        onChange={(e) => onOrdenarChange(e.target.value)}
                        className="w-full"
                    />
                </div>

                <div className="min-w-0">
                    <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wide text-white/40">Status</label>
                    <Select
                        isMulti
                        options={statuses.map((s) => ({ value: s.id, label: s.nome }))}
                        value={statuses.filter((s) => selectedStatuses.includes(s.id)).map((s) => ({ value: s.id, label: s.nome }))}
                        onChange={(val: unknown) =>
                            onStatusChange(Array.isArray(val) ? (val as { value: number }[]).map((v) => v.value) : [])
                        }
                        styles={customSelectStyles}
                        placeholder="Status da obra…"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => 'Nenhum status'}
                    />
                </div>
            </div>

            <div className="min-w-0">
                <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wide text-white/40">Tags</label>
                <TagSelector
                    tags={tags}
                    selected={selectedTags}
                    onChange={onTagsChange}
                    loading={loadingTags}
                />
            </div>

            <div className="border-t border-white/10 pt-4">
                <label className="mb-2 block text-[10px] font-medium uppercase tracking-wide text-white/40">Quantidade de capítulos</label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                        <span className="mb-1.5 block text-[10px] text-white/35">Mínimo</span>
                        <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            className="rounded-xl bg-white/[0.03] py-2.5"
                            value={draftMin}
                            onChange={(e) => setDraftMin(e.target.value)}
                            onBlur={commitCapitulos}
                            onKeyDown={(e) => e.key === 'Enter' && commitCapitulos()}
                            placeholder="0"
                            aria-label="Mínimo de capítulos"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="mb-1.5 block text-[10px] text-white/35">Máximo</span>
                        <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            className="rounded-xl bg-white/[0.03] py-2.5"
                            value={draftMax}
                            onChange={(e) => setDraftMax(e.target.value)}
                            onBlur={commitCapitulos}
                            onKeyDown={(e) => e.key === 'Enter' && commitCapitulos()}
                            placeholder={String(CAPITULOS_MAX_PADRAO)}
                            aria-label="Máximo de capítulos"
                        />
                    </div>
                    {capitulosFiltroAtivo ? (
                        <button
                            type="button"
                            onClick={() => {
                                setDraftMin('0');
                                setDraftMax(String(CAPITULOS_MAX_PADRAO));
                                onCapitulosChange(0, CAPITULOS_MAX_PADRAO);
                            }}
                            className="shrink-0 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-white/70 transition-colors hover:border-orange-500/40 hover:text-white"
                        >
                            Limpar
                        </button>
                    ) : null}
                </div>
                <p className="mt-2 text-[10px] text-white/30">
                    Valor máximo padrão ({CAPITULOS_MAX_PADRAO}) equivale a “sem teto” na busca; use um número maior se precisar incluir obras muito longas.
                </p>
            </div>
        </div>
    );
};
